/**
 * Frontend input sanitization utilities
 */

/**
 * Sanitize text input to prevent XSS and other attacks
 * @param {string} input - The input string to sanitize
 * @param {number} maxLength - Maximum allowed length
 * @returns {string} Sanitized string
 */
export const sanitizeText = (input, maxLength = 1000) => {
    if (typeof input !== 'string') return '';
    
    return input
        .trim()
        .substring(0, maxLength)
        .replace(/[<>]/g, '') // Remove potential HTML tags
        .replace(/javascript:/gi, '') // Remove javascript: protocol
        .replace(/on\w+=/gi, '') // Remove event handlers
        .replace(/script/gi, '') // Remove script tags
        .replace(/iframe/gi, '') // Remove iframe tags
        .replace(/object/gi, '') // Remove object tags
        .replace(/embed/gi, '') // Remove embed tags
        .replace(/link/gi, '') // Remove link tags
        .replace(/meta/gi, '') // Remove meta tags
        .replace(/data:/gi, '') // Remove data: protocol
        .replace(/vbscript:/gi, '') // Remove vbscript: protocol
        .replace(/file:/gi, ''); // Remove file: protocol
};

/**
 * Sanitize array of strings
 * @param {Array} arr - Array of strings to sanitize
 * @param {number} maxLength - Maximum allowed length per item
 * @returns {Array} Sanitized array
 */
export const sanitizeArray = (arr, maxLength = 1000) => {
    if (!Array.isArray(arr)) return [];
    
    return arr
        .filter(item => typeof item === 'string')
        .map(item => sanitizeText(item, maxLength))
        .filter(item => item.length > 0);
};

/**
 * Validate display name
 * @param {string} name - Display name to validate
 * @returns {Object} Validation result with isValid and error message
 */
export const validateDisplayName = (name) => {
    if (!name || typeof name !== 'string') {
        return { isValid: false, error: 'Display name is required' };
    }
    
    const trimmed = name.trim();
    
    if (trimmed.length < 1) {
        return { isValid: false, error: 'Display name cannot be empty' };
    }
    
    if (trimmed.length > 100) {
        return { isValid: false, error: 'Display name must be less than 100 characters' };
    }
    
    // Allow letters, numbers, spaces, hyphens, underscores, and dots
    const nameRegex = /^[a-zA-Z0-9\s\-_.]+$/;
    if (!nameRegex.test(trimmed)) {
        return { isValid: false, error: 'Display name can only contain letters, numbers, spaces, hyphens, underscores, and dots' };
    }
    
    return { isValid: true, error: null };
};

/**
 * Validate activity preferences
 * @param {Array} activities - Array of activity strings
 * @returns {Object} Validation result
 */
export const validateActivityPreferences = (activities) => {
    if (!Array.isArray(activities)) {
        return { isValid: false, error: 'Activity preferences must be an array' };
    }
    
    if (activities.length > 20) {
        return { isValid: false, error: 'You can select up to 20 activities' };
    }
    
    for (const activity of activities) {
        if (typeof activity !== 'string') {
            return { isValid: false, error: 'All activities must be strings' };
        }
        
        if (activity.length > 100) {
            return { isValid: false, error: 'Activity names must be less than 100 characters' };
        }
        
        // Allow letters, numbers, spaces, hyphens, underscores, and ampersands
        const activityRegex = /^[a-zA-Z0-9\s\-_&]+$/;
        if (!activityRegex.test(activity)) {
            return { isValid: false, error: 'Activity names can only contain letters, numbers, spaces, hyphens, underscores, and ampersands' };
        }
    }
    
    return { isValid: true, error: null };
};

/**
 * Validate dietary restrictions
 * @param {Array} restrictions - Array of dietary restriction strings
 * @returns {Object} Validation result
 */
export const validateDietaryRestrictions = (restrictions) => {
    if (!Array.isArray(restrictions)) {
        return { isValid: false, error: 'Dietary restrictions must be an array' };
    }
    
    if (restrictions.length > 15) {
        return { isValid: false, error: 'You can select up to 15 dietary restrictions' };
    }
    
    for (const restriction of restrictions) {
        if (typeof restriction !== 'string') {
            return { isValid: false, error: 'All dietary restrictions must be strings' };
        }
        
        if (restriction.length > 50) {
            return { isValid: false, error: 'Dietary restriction names must be less than 50 characters' };
        }
        
        // Allow letters, numbers, spaces, and hyphens
        const restrictionRegex = /^[a-zA-Z0-9\s-]+$/;
        if (!restrictionRegex.test(restriction)) {
            return { isValid: false, error: 'Dietary restriction names can only contain letters, numbers, spaces, and hyphens' };
        }
    }
    
    return { isValid: true, error: null };
};

/**
 * Validate custom text fields
 * @param {string} text - Custom text to validate
 * @param {string} fieldName - Name of the field for error messages
 * @param {number} maxLength - Maximum allowed length
 * @returns {Object} Validation result
 */
export const validateCustomText = (text, fieldName = 'Text', maxLength = 500) => {
    if (!text || typeof text !== 'string') {
        return { isValid: true, error: null }; // Custom fields are optional
    }
    
    const trimmed = text.trim();
    
    if (trimmed.length > maxLength) {
        return { isValid: false, error: `${fieldName} must be less than ${maxLength} characters` };
    }
    
    // Allow letters, numbers, spaces, hyphens, underscores, dots, and commas
    const textRegex = /^[a-zA-Z0-9\s\-_.,]+$/;
    if (!textRegex.test(trimmed)) {
        return { isValid: false, error: `${fieldName} can only contain letters, numbers, spaces, hyphens, underscores, dots, and commas` };
    }
    
    return { isValid: true, error: null };
};

/**
 * Validate enum values
 * @param {string} value - Value to validate
 * @param {Array} allowedValues - Array of allowed values
 * @param {string} fieldName - Name of the field for error messages
 * @returns {Object} Validation result
 */
export const validateEnum = (value, allowedValues, fieldName) => {
    if (!allowedValues.includes(value)) {
        return { 
            isValid: false, 
            error: `${fieldName} must be one of: ${allowedValues.join(', ')}` 
        };
    }
    
    return { isValid: true, error: null };
};

/**
 * Validate time preferences
 * @param {Array} times - Array of time preference strings
 * @returns {Object} Validation result
 */
export const validateTimePreferences = (times) => {
    if (!Array.isArray(times)) {
        return { isValid: false, error: 'Time preferences must be an array' };
    }
    
    if (times.length > 10) {
        return { isValid: false, error: 'You can select up to 10 time preferences' };
    }
    
    const validTimes = [
        'Early Morning', 'Morning', 'Afternoon', 'Evening', 'Night', 'Late Night'
    ];
    
    for (const time of times) {
        if (!validTimes.includes(time)) {
            return { isValid: false, error: 'Invalid time preference selected' };
        }
    }
    
    return { isValid: true, error: null };
};

/**
 * Validate day preferences
 * @param {Array} days - Array of day preference strings
 * @returns {Object} Validation result
 */
export const validateDayPreferences = (days) => {
    if (!Array.isArray(days)) {
        return { isValid: false, error: 'Day preferences must be an array' };
    }
    
    if (days.length > 7) {
        return { isValid: false, error: 'You can select up to 7 day preferences' };
    }
    
    const validDays = [
        'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
    ];
    
    for (const day of days) {
        if (!validDays.includes(day)) {
            return { isValid: false, error: 'Invalid day preference selected' };
        }
    }
    
    return { isValid: true, error: null };
};

/**
 * Comprehensive form validation for setup wizard
 * @param {Object} formData - Form data to validate
 * @returns {Object} Validation result with errors array
 */
export const validateSetupWizardForm = (formData) => {
    const errors = [];
    
    // Validate display name
    const displayNameValidation = validateDisplayName(formData.display_name);
    if (!displayNameValidation.isValid) {
        errors.push({ field: 'display_name', message: displayNameValidation.error });
    }
    
    // Validate activity preferences
    const activityValidation = validateActivityPreferences(formData.activityPreferences);
    if (!activityValidation.isValid) {
        errors.push({ field: 'activityPreferences', message: activityValidation.error });
    }
    
    // Validate dietary restrictions
    const dietaryValidation = validateDietaryRestrictions(formData.dietaryRestrictions);
    if (!dietaryValidation.isValid) {
        errors.push({ field: 'dietaryRestrictions', message: dietaryValidation.error });
    }
    
    // Validate custom fields
    const customActivitiesValidation = validateCustomText(formData.customActivities, 'Custom activities');
    if (!customActivitiesValidation.isValid) {
        errors.push({ field: 'customActivities', message: customActivitiesValidation.error });
    }
    
    const customDietaryValidation = validateCustomText(formData.customDietary, 'Custom dietary requirements');
    if (!customDietaryValidation.isValid) {
        errors.push({ field: 'customDietary', message: customDietaryValidation.error });
    }
    
    // Validate enum fields
    const travelRadiusValidation = validateEnum(formData.travelRadius, ['small', 'medium', 'large'], 'Travel radius');
    if (!travelRadiusValidation.isValid) {
        errors.push({ field: 'travelRadius', message: travelRadiusValidation.error });
    }
    
    const budgetValidation = validateEnum(formData.budgetRange, ['low', 'medium', 'high', 'luxury'], 'Budget range');
    if (!budgetValidation.isValid) {
        errors.push({ field: 'budgetRange', message: budgetValidation.error });
    }
    
    const groupSizeValidation = validateEnum(formData.comfortableGroupSize, ['small', 'medium', 'large'], 'Group size');
    if (!groupSizeValidation.isValid) {
        errors.push({ field: 'comfortableGroupSize', message: groupSizeValidation.error });
    }
    
    // Validate time and day preferences
    const timeValidation = validateTimePreferences(formData.preferredTimes);
    if (!timeValidation.isValid) {
        errors.push({ field: 'preferredTimes', message: timeValidation.error });
    }
    
    const dayValidation = validateDayPreferences(formData.dayPreferences);
    if (!dayValidation.isValid) {
        errors.push({ field: 'dayPreferences', message: dayValidation.error });
    }
    
    return {
        isValid: errors.length === 0,
        errors
    };
};

/**
 * Sanitize setup wizard form data
 * @param {Object} formData - Form data to sanitize
 * @returns {Object} Sanitized form data
 */
export const sanitizeSetupWizardForm = (formData) => {
    return {
        ...formData,
        display_name: sanitizeText(formData.display_name, 100),
        activityPreferences: sanitizeArray(formData.activityPreferences, 100),
        dietaryRestrictions: sanitizeArray(formData.dietaryRestrictions, 50),
        preferredTimes: sanitizeArray(formData.preferredTimes, 50),
        dayPreferences: sanitizeArray(formData.dayPreferences, 20),
        customActivities: sanitizeText(formData.customActivities, 500),
        customDietary: sanitizeText(formData.customDietary, 500),
        customAreas: sanitizeText(formData.customAreas, 500),
        // Enum values don't need sanitization as they're validated against allowed values
        travelRadius: formData.travelRadius,
        budgetRange: formData.budgetRange,
        comfortableGroupSize: formData.comfortableGroupSize
    };
};
